import {
  call, cancel, cancelled, fork, put, select, take,
} from 'redux-saga/effects';
import { Task } from 'redux-saga';
import {
  AIChatApiError,
  AIChatProtocolError,
  AIChatWireResponse,
  deleteAIChatConversation,
  sendAIApartmentChatMessage,
  sendAIApartmentChatToolResult,
} from 'src/api/ai-chat';
import { AI_CHAT_CONTEXT_LIMIT_CODE, AI_CHAT_ERROR_TEXT } from 'src/constants/ai-chat';
import { randomHash } from 'src/helpers';
import { IAction } from 'src/typings';
import {
  AIChatClosePayload,
  AIChatComplexContext,
  AIChatMessage,
  AIChatProgressStepDetails,
} from 'src/typings/ai-chat';
import { IFilterAllData, ListOfKeysFilterAllData } from 'src/typings/filter';
import {
  ActionTypes,
  advanceProgressTrace,
  clearProgressTrace,
  completeProgressTrace,
  contextLimitReached,
  failProgressTrace,
  invalidateConversation,
  sendMessageFailure,
  sendMessageSuccess,
  setConversationId,
  startProgressTrace,
} from './actions';
import {
  formatApartmentSearchSummary,
  sanitizeAIChatFinalResponseText,
  sanitizeAIChatProgressText,
} from './progress';
import {
  getAIChatCandidates,
  getAIChatComplexContext,
  getAIChatConversationContext,
  getAIChatConversationId,
  getAIChatMessages,
} from './selectors';
import {
  normalizeAIChatSearchArguments,
  sanitizeAIChatSearchArgumentsForDisplay,
  searchApartments,
  selectRecommendedApartmentSnapshots,
} from './search-apartments';

const SEARCH_APARTMENTS_TOOL_NAME = 'search_apartments';
const INVALID_TOOL_CALL_ERROR = 'Invalid AI chat tool call';
const UNSAFE_FINAL_RESPONSE_ERROR = 'Unsafe AI chat final response';
const MAX_TOOL_ROUNDS = 5;

const getErrorMessage = (error: unknown) => {
  const message = typeof error === 'object' && error !== null && 'message' in error
    ? (error as { message?: unknown }).message
    : undefined;

  return typeof message === 'string' && message.length > 0 ? message : AI_CHAT_ERROR_TEXT;
};

const getLatestUserMessage = (messages: AIChatMessage[]) => (
  [...messages].reverse().find(({ role }) => role === 'user')
);

export function* deleteConversationWorker(conversationId: string): Generator<unknown, void, unknown> {
  try {
    yield call(deleteAIChatConversation, conversationId);
  } catch (error) {
    console.warn('Unable to delete AI chat conversation', error);
  }
}

export function* sendMessageWorker(
  { type, payload }: IAction<string>,
  now: () => number = Date.now,
): Generator<unknown, void, unknown> {
  let text = payload || '';

  if (type === ActionTypes.RETRY_LAST_MESSAGE) {
    const messages = (yield select(getAIChatMessages)) as AIChatMessage[];
    const latestUserMessage = getLatestUserMessage(messages);
    if (!latestUserMessage) return;
    ({ text } = latestUserMessage);
  }

  const traceId = randomHash();
  let progressStep = 0;
  const nextStepId = () => {
    const stepId = `${traceId}-step-${progressStep}`;
    progressStep += 1;
    return stepId;
  };
  let activeConversationId: string | null = null;
  let requestedConversationId: string | null = null;
  let latestResponseConversationId: string | null = null;
  let cancellationRequested = false;
  const cleanupRequestedIds = new Set<string>();
  const cleanupConversationId = (conversationId: string) => {
    if (conversationId === requestedConversationId || cleanupRequestedIds.has(conversationId)) return;
    cleanupRequestedIds.add(conversationId);
    Promise.resolve().then(() => deleteAIChatConversation(conversationId)).catch((error) => {
      console.warn('Unable to delete AI chat conversation', error);
    });
  };
  const recordResponseConversationId = (conversationId: string) => {
    latestResponseConversationId = conversationId;
    if (cancellationRequested) cleanupConversationId(conversationId);
  };
  const responseOptions = {
    onConversationId: recordResponseConversationId,
    onResponse: (response: AIChatWireResponse) => {
      recordResponseConversationId(response.conversationId);
    },
  };
  let toolRoundStarted = false;

  try {
    yield put(startProgressTrace({ traceId, stepId: nextStepId(), startedAt: now() }));

    const initialConversationId = (yield select(getAIChatConversationId)) as string | null;
    const storedConversationContext = (yield select(
      getAIChatConversationContext,
    )) as AIChatComplexContext | null;
    const liveContext = (yield select(getAIChatComplexContext)) as AIChatComplexContext | null;
    const candidates = (yield select(getAIChatCandidates)) as IFilterAllData[];
    let conversationId = initialConversationId;
    let context: AIChatComplexContext;
    let storedConversationId = conversationId;
    let toolRoundCount = 0;
    let lastTotalMatches = 0;

    try {
      if (!liveContext) throw new Error('AI chat context is unavailable');
      context = liveContext;
      if (initialConversationId) {
        if (!storedConversationContext
          || liveContext.primaryPriceCurrency !== storedConversationContext.primaryPriceCurrency) {
          yield call(deleteConversationWorker, initialConversationId);
          yield put(invalidateConversation());
          conversationId = null;
          storedConversationId = null;
        } else {
          context = storedConversationContext;
        }
      }
      activeConversationId = conversationId;
      requestedConversationId = conversationId;
      const { primaryPriceCurrency } = context;

      let response = (yield call(sendAIApartmentChatMessage, {
        text,
        conversationId,
        context,
      }, responseOptions)) as AIChatWireResponse;

      while (response.status === 'tool_calls') {
        toolRoundStarted = true;
        toolRoundCount += 1;
        if (toolRoundCount > MAX_TOOL_ROUNDS) {
          throw new Error('Exceeded AI chat tool round limit');
        }
        activeConversationId = response.conversationId;
        if (activeConversationId !== storedConversationId) {
          yield put(setConversationId({
            conversationId: activeConversationId,
            conversationContext: context,
          }));
          storedConversationId = activeConversationId;
        }

        if (response.calls.length !== 1 || response.calls[0].name !== SEARCH_APARTMENTS_TOOL_NAME) {
          throw new Error(INVALID_TOOL_CALL_ERROR);
        }

        const toolCall = response.calls[0];
        const searchArguments = normalizeAIChatSearchArguments(toolCall.arguments, text);
        const searchArgumentsForDisplay = sanitizeAIChatSearchArgumentsForDisplay(searchArguments, candidates);
        let searchDetail: string | undefined;
        try {
          searchDetail = formatApartmentSearchSummary(searchArgumentsForDisplay);
        } catch {
          searchDetail = undefined;
        }
        const searchStartedAt = now();
        const searchQueryDetails: AIChatProgressStepDetails = {
          type: 'search',
          query: searchArgumentsForDisplay,
        };
        const progressText = sanitizeAIChatProgressText(response.text, [
          context.complex.id,
          ...candidates.map((candidate) => String(candidate[ListOfKeysFilterAllData.id])),
        ]);
        if (toolRoundCount > 1) {
          yield put(advanceProgressTrace({
            traceId,
            finishedAt: searchStartedAt,
            nextStep: {
              id: nextStepId(),
              kind: 'refining',
              startedAt: searchStartedAt,
              ...(progressText ? { details: { type: 'llm', text: progressText } } : {}),
            },
          }));
          yield put(advanceProgressTrace({
            traceId,
            finishedAt: searchStartedAt,
            nextStep: {
              id: nextStepId(),
              kind: 'searching',
              startedAt: searchStartedAt,
              detail: searchDetail,
              details: searchQueryDetails,
            },
          }));
        } else {
          yield put(advanceProgressTrace({
            traceId,
            finishedAt: searchStartedAt,
            ...(progressText ? {
              completedDetails: { type: 'llm', text: progressText },
            } : {}),
            nextStep: {
              id: nextStepId(),
              kind: 'searching',
              startedAt: searchStartedAt,
              detail: searchDetail,
              details: searchQueryDetails,
            },
          }));
        }
        let searchResult: ReturnType<typeof searchApartments> | null = null;
        let localToolError: unknown;
        try {
          searchResult = (yield call(
            searchApartments,
            searchArguments,
            candidates,
            primaryPriceCurrency,
            text,
          )) as ReturnType<typeof searchApartments>;
        } catch (error) {
          localToolError = error;
        }

        const reviewStartedAt = now();
        const searchResultDetails: AIChatProgressStepDetails = searchResult
          ? { type: 'search', query: searchArgumentsForDisplay, result: searchResult }
          : {
            type: 'search',
            query: searchArgumentsForDisplay,
            error: getErrorMessage(localToolError),
          };
        yield put(advanceProgressTrace({
          traceId,
          finishedAt: reviewStartedAt,
          completedState: searchResult ? 'completed' : 'failed',
          completedDetails: searchResultDetails,
          nextStep: {
            id: nextStepId(),
            kind: 'reviewing',
            startedAt: reviewStartedAt,
            details: searchResultDetails,
          },
        }));

        if (searchResult) {
          lastTotalMatches = searchResult.total;
          response = (yield call(sendAIApartmentChatToolResult, {
            conversationId: activeConversationId,
            toolCallId: toolCall.toolCallId,
            status: 'success',
            data: {
              total: searchResult.total,
              currencies: searchResult.currencies,
              apartments: searchResult.apartments,
            },
            apartmentIds: searchResult.apartmentIds,
          }, responseOptions)) as AIChatWireResponse;
        } else {
          response = (yield call(sendAIApartmentChatToolResult, {
            conversationId: activeConversationId,
            toolCallId: toolCall.toolCallId,
            status: 'error',
            error: getErrorMessage(localToolError).slice(0, 1000),
          }, responseOptions)) as AIChatWireResponse;
        }
      }

      activeConversationId = response.conversationId;
      const finalText = sanitizeAIChatFinalResponseText(response.text, [
        context.complex.id,
        ...candidates.map((candidate) => String(candidate[ListOfKeysFilterAllData.id])),
      ]);
      if (!finalText) {
        throw new AIChatProtocolError(UNSAFE_FINAL_RESPONSE_ERROR, response.conversationId);
      }
      const apartments = selectRecommendedApartmentSnapshots(
        response.recommendedApartmentIds,
        candidates,
        6,
      );
      if (toolRoundStarted) {
        yield put(completeProgressTrace({ traceId, finishedAt: now() }));
      }
      yield put(sendMessageSuccess({
        conversationId: response.conversationId,
        conversationContext: context,
        includeProgressTrace: toolRoundStarted,
        message: {
          id: randomHash(),
          text: finalText,
          criteria: [],
          totalMatches: lastTotalMatches,
          apartments,
        },
      }));
    } catch (error) {
      if (error instanceof AIChatApiError && error.code === AI_CHAT_CONTEXT_LIMIT_CODE) {
        if (!requestedConversationId && error.conversationId && liveContext) {
          yield put(setConversationId({
            conversationId: error.conversationId,
            conversationContext: liveContext,
          }));
        }
        yield put(contextLimitReached());
        return;
      }
      yield put(failProgressTrace({ traceId, finishedAt: now() }));
      if (error instanceof AIChatApiError && error.status === 404) {
        yield put(invalidateConversation());
      } else if (error instanceof AIChatProtocolError && error.conversationId) {
        yield call(deleteConversationWorker, error.conversationId);
        yield put(invalidateConversation());
      } else if (toolRoundStarted && activeConversationId) {
        yield call(deleteConversationWorker, activeConversationId);
        yield put(invalidateConversation());
      }
      yield put(sendMessageFailure(getErrorMessage(error)));
    }
  } finally {
    const wasCancelled = (yield cancelled()) as boolean;
    if (wasCancelled) {
      cancellationRequested = true;
      if (activeConversationId) {
        cleanupRequestedIds.add(activeConversationId);
        yield call(deleteConversationWorker, activeConversationId);
      }
      if (latestResponseConversationId) {
        const responseConversationId = latestResponseConversationId;
        if (responseConversationId !== requestedConversationId
          && !cleanupRequestedIds.has(responseConversationId)) {
          cleanupRequestedIds.add(responseConversationId);
          yield call(deleteConversationWorker, responseConversationId);
        }
      }
      yield put(clearProgressTrace({ traceId }));
    }
  }
}

export function* aiChatWatchSaga(): Generator<unknown, never, unknown> {
  let currentTask: Task | undefined;

  while (true) {
    const action = (yield take([
      ActionTypes.SEND_MESSAGE_REQUEST,
      ActionTypes.RETRY_LAST_MESSAGE,
      ActionTypes.CLOSE_CHAT,
      ActionTypes.NEW_CHAT,
    ])) as IAction<string | AIChatClosePayload>;

    if (currentTask) {
      yield cancel(currentTask);
      currentTask = undefined;
    }

    if (action.type === ActionTypes.SEND_MESSAGE_REQUEST
      || action.type === ActionTypes.RETRY_LAST_MESSAGE) {
      currentTask = (yield fork(sendMessageWorker, action as IAction<string>)) as Task;
    }

    if (action.type === ActionTypes.CLOSE_CHAT || action.type === ActionTypes.NEW_CHAT) {
      const closePayload = action.payload as AIChatClosePayload | undefined;
      if (closePayload?.conversationId) {
        yield fork(deleteConversationWorker, closePayload.conversationId);
      }
    }
  }
}
